import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { WarningCircleIcon } from "@phosphor-icons/react";

import { colors } from "../../../../../styles/design-system";
import type { ActivityIssueFinding } from "../types/reviewTypes";

interface IssueFindingsListProps {
  findings?: ActivityIssueFinding[];
}

function toDimensionHeading(dimension?: string, fallbackLabel?: string): string {
  if (!dimension) {
    return fallbackLabel ?? "General";
  }

  return dimension
    .split(/[_-]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function IssueFindingsList({ findings }: IssueFindingsListProps) {
  if (!findings || findings.length === 0) {
    return null;
  }

  const groupedFindings = findings.reduce<Record<string, ActivityIssueFinding[]>>((acc, finding) => {
    const key = finding.dimension ?? "general";
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(finding);
    return acc;
  }, {});

  return (
    <View style={styles.list}>
      {Object.entries(groupedFindings).map(([groupKey, groupItems]) => (
        <View key={groupKey} style={styles.group}>
          <Text style={styles.groupLabel}>
            {toDimensionHeading(groupItems[0]?.dimension, groupItems[0]?.dimensionLabel)}
          </Text>
          {groupItems.map((finding, index) => (
            <View key={`${groupKey}-${index}`} style={styles.row}>
              <View style={styles.iconSlot}>
                <WarningCircleIcon size={13} color={colors.warning[700]} weight="regular" />
              </View>
              <View style={styles.findingBody}>
                <Text style={styles.findingText}>{finding.text}</Text>
                {finding.dimensionLabel && (
                  <View style={styles.labelChip}>
                    <Text style={styles.labelChipText}>{finding.dimensionLabel}</Text>
                  </View>
                )}
              </View>
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  list: {
    gap: 10,
    marginTop: 4,
  },
  group: {
    gap: 6,
  },
  groupLabel: {
    fontFamily: "Inter",
    fontSize: 12,
    fontWeight: "700",
    lineHeight: 16,
    color: colors.grey[800],
  },
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  iconSlot: {
    width: 16,
    height: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  findingBody: {
    flex: 1,
    gap: 4,
    minWidth: 0,
  },
  findingText: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    fontFamily: "Inter",
    fontSize: 12,
    fontWeight: "500",
    lineHeight: 18,
    color: colors.grey[700],
  },
  labelChip: {
    alignSelf: "flex-start",
    backgroundColor: colors.warning[50],
    borderRadius: 999,
    borderWidth: 1,
    borderColor: colors.warning[200],
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  labelChipText: {
    fontFamily: "Inter",
    fontSize: 10,
    fontWeight: "600",
    lineHeight: 14,
    color: colors.warning[800],
  },
});

export default IssueFindingsList;
